import { a as createAction, c as createAsyncThunk, e as commonActions, r as requestJSON, b as request, aF as decoratedNoteFactory, bR as loadWorkitemInAmplify, bS as showSubmittedFeedback, bT as loadNextOrPreviousCase, Q as amplifyFeedback, n as normalizeDescriptions } from './code-bench-CmxRFbZe.mjs';
import { M as Methods, B as BenchMode, F as FeedbackMeta, O as CoderNotesLookupTypeName } from './enums-CKrQOJk5.mjs';
import { d as convertToDateFormat } from './date-time-IA6Oj6Ns.mjs';
import { c as codePredicates } from './transaction-updates-IKU_rSP_.mjs';
import { u as uniqBy } from './object-utils-CWxdM56M.mjs';

const navigationIntent = createAction('NAVIGATION_INTENT');

var navigationIntent_action = /*#__PURE__*/Object.freeze({
  __proto__: null,
  navigationIntent: navigationIntent
});

function mapAttribute(attr) {
    return {
        id: attr.id,
        entityId: attr.entityId,
        entityType: attr.entityType,
        questionId: attr.questionId,
        answerValueId: attr.answerValueId,
        answerDisplayText: attr.answerDisplayText
    };
}
function mapSpan(span) {
    var _span_rubricAttributes;
    return {
        id: span.id,
        beginOffset: span.beginOffset,
        endOffset: span.endOffset,
        coveredText: span.coveredText,
        documentId: span.documentId,
        attributes: ((_span_rubricAttributes = span.rubricAttributes) !== null && _span_rubricAttributes !== void 0 ? _span_rubricAttributes : []).map(mapAttribute)
    };
}
/**
 * Build workItemData from the first invoice (which has work item fields merged when loaded).
 */ function mapWorkItemData(firstInvoice) {
    var _firstInvoice_workitemId, _firstInvoice_sdCaseId;
    if (!firstInvoice) return undefined;
    return {
        id: (_firstInvoice_workitemId = firstInvoice.workitemId) !== null && _firstInvoice_workitemId !== void 0 ? _firstInvoice_workitemId : firstInvoice.id,
        caseId: (_firstInvoice_sdCaseId = firstInvoice.sdCaseId) !== null && _firstInvoice_sdCaseId !== void 0 ? _firstInvoice_sdCaseId : firstInvoice.caseId,
        caseJsonId: firstInvoice.caseJsonId,
        tenantUid: firstInvoice.tenantUid,
        processInstanceGuid: firstInvoice.processInstanceGuid,
        caseRunUid: firstInvoice.caseRunUid,
        serviceLine: firstInvoice.serviceLine,
        healthSystemTenantId: firstInvoice.healthSystemTenantId,
        status: firstInvoice.status,
        importId: firstInvoice.importId,
        ruleId: firstInvoice.ruleId,
        ruleName: firstInvoice.ruleName,
        ruleCollectionId: firstInvoice.ruleCollectionId
    };
}
const mapCode = (codeType, code)=>{
    var _code_sequenceNumber, _code_attributes, _code_spans;
    return {
        codeType: codeType,
        id: code.id,
        code: code.code,
        original: code.original,
        sequenceNumber: (_code_sequenceNumber = code.sequenceNumber) !== null && _code_sequenceNumber !== void 0 ? _code_sequenceNumber : 0,
        attributes: ((_code_attributes = code.attributes) !== null && _code_attributes !== void 0 ? _code_attributes : []).map(mapAttribute),
        spans: ((_code_spans = code.spans) !== null && _code_spans !== void 0 ? _code_spans : []).map(mapSpan)
    };
};
/**
 * Maps grade-bench panel state to ResolverInputDto for POST /resolver/resolve.
 */ function gradeBenchStateToResolverInput(state) {
    var _state_invoices, _state_caseAttributes, _state_looseSpans;
    const codes = [];
    const invoices = (_state_invoices = state.invoices) !== null && _state_invoices !== void 0 ? _state_invoices : [];
    invoices.forEach((invoice, invIdx)=>{
        var _invoice_transactions;
        ((_invoice_transactions = invoice.transactions) !== null && _invoice_transactions !== void 0 ? _invoice_transactions : []).forEach((tx, txIdx)=>{
            var _tx_procedures, _tx_diagnoses, _tx_modifiers;
            ((_tx_procedures = tx.procedures) !== null && _tx_procedures !== void 0 ? _tx_procedures : []).forEach((p)=>codes.push(mapCode('PROCEDURE', p)));
            ((_tx_diagnoses = tx.diagnoses) !== null && _tx_diagnoses !== void 0 ? _tx_diagnoses : []).forEach((d)=>codes.push(mapCode('DIAGNOSIS', d)));
            ((_tx_modifiers = tx.modifiers) !== null && _tx_modifiers !== void 0 ? _tx_modifiers : []).forEach((m)=>codes.push(mapCode('MODIFIER', m)));
        });
    });
    const caseAttributes = ((_state_caseAttributes = state.caseAttributes) !== null && _state_caseAttributes !== void 0 ? _state_caseAttributes : []).map(mapAttribute);
    const workItemData = mapWorkItemData(invoices[0]);
    const looseSpans = ((_state_looseSpans = state.looseSpans) !== null && _state_looseSpans !== void 0 ? _state_looseSpans : []).map(mapSpan);
    return {
        codes,
        caseAttributes,
        workItemData,
        looseSpans
    };
}

const QUALITY_ASSESSMENT_BASE_PATH = '/cmx/quality-assessment/';
/**
 * Builds the quality assessment worklist path with optional projectId/status
 * query params. Used when navigating back to the CQA list from the grading
 * screen (e.g. direct link or after submit). Callers are responsible for
 * dispatching navigation with the returned path.
 * Accepts any context that may have returnToWorklistQuery (e.g. augmented CursorAPIContext).
 */ function buildQualityAssessmentWorklistPath(cursorAPIContext) {
    const returnToWorklistQuery = cursorAPIContext === null || cursorAPIContext === void 0 ? void 0 : cursorAPIContext.returnToWorklistQuery;
    const params = new URLSearchParams();
    if (returnToWorklistQuery === null || returnToWorklistQuery === void 0 ? void 0 : returnToWorklistQuery.projectId) {
        params.set('projectId', returnToWorklistQuery.projectId);
    }
    if (returnToWorklistQuery === null || returnToWorklistQuery === void 0 ? void 0 : returnToWorklistQuery.status) {
        params.set('status', returnToWorklistQuery.status);
    }
    return params.toString() ? `${QUALITY_ASSESSMENT_BASE_PATH}?${params.toString()}` : QUALITY_ASSESSMENT_BASE_PATH;
}

const renderValue = (value)=>{
    // Check if value is an object and not null or an array
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Handle the object case, e.g., by rendering its properties
        // Adjust this logic based on how you want to display the object
        return `${JSON.stringify(value)}`;
    }
    // Return the value directly if it's not an object
    return value;
};
function getValueAtPath(object, path, defaultValue) {
    const pathArray = Array.isArray(path) ? path : [
        path
    ];
    let currentObject = object;
    for (let key of pathArray){
        if (currentObject == null || currentObject[key] === undefined) {
            // If path does not exist return undefined or a safer alternative such as the string 'N/A'
            return defaultValue;
        }
        currentObject = currentObject[key];
    }
    return renderValue(currentObject);
}
//Returns true if succesfull, false otherwise
function changeValueAtPath(object, path, newValue) {
    const pathArray = Array.isArray(path) ? path : [
        path
    ]; // Ensure path is an array
    let currentObject = object;
    for(let i = 0; i < pathArray.length; i++){
        const key = pathArray[i];
        // If we're at the last key, set the new value
        if (i === pathArray.length - 1) {
            currentObject[key] = newValue;
            return true;
        }
        // If the next key doesn't exist or isn't an object, abort the modification
        if (typeof currentObject[key] !== 'object' || currentObject[key] === null) {
            return false;
        }
        // Move to the next level in the object
        currentObject = currentObject[key];
    }
}
function getOptionsFromTree(values, optionsTree, optionsTreePath, // For a given path, if the key is not found, use this key as the default to continue searching
defaultKey = '*') {
    if (!optionsTree || !optionsTreePath) {
        return undefined;
    }
    let currentTree = optionsTree;
    for (const path of optionsTreePath){
        // Resolve the key from the values object
        const key = path.reduce((acc, current)=>acc === null || acc === void 0 ? void 0 : acc[current], values);
        const coercedKey = typeof key === 'boolean' ? String(key) : key;
        if (typeof coercedKey !== 'string') {
            return undefined; // Ensure the key is a string after coercion
        }
        // Traverse the optionsTree
        if (typeof currentTree === 'object' && currentTree[coercedKey]) {
            currentTree = currentTree[coercedKey];
        } else if (typeof currentTree === 'object' && currentTree[defaultKey]) {
            currentTree = currentTree[defaultKey];
        } else {
            return undefined; // If the key doesn't exist in the optionsTree, return undefined
        }
    }
    // Return the result, ensuring it's a string array or undefined
    return Array.isArray(currentTree) ? currentTree : undefined;
}

var pathUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  changeValueAtPath: changeValueAtPath,
  getOptionsFromTree: getOptionsFromTree,
  getValueAtPath: getValueAtPath
});

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const { isSelectionCPT, isSelectionICD, isSelectionModifier } = codePredicates;
const { GradeBenchPanel } = commonActions;
const fetchRiskCategories = createAsyncThunk(GradeBenchPanel.FETCH_RISK_CATEGORIES, async ()=>{
    const todaysDate = convertToDateFormat(new Date());
    const ModRowData = await requestJSON(`/dictionary/row/unrestricted/v2?tenantId=BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C&effectiveDate=${todaysDate}&dictionaryName=TempRiskCategoriesMODV2`, {
        method: Methods.GET
    });
    const CptRowData = await requestJSON(`/dictionary/row/unrestricted/v2?tenantId=BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C&effectiveDate=${todaysDate}&dictionaryName=TempRiskCategoriesCPT`, {
        method: Methods.GET
    });
    const IcdRowData = await requestJSON(`/dictionary/row/unrestricted/v2?tenantId=BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C&effectiveDate=${todaysDate}&dictionaryName=TempRiskCategoriesICD`, {
        method: Methods.GET
    });
    return {
        ModRowData,
        CptRowData,
        IcdRowData
    };
});
/*
we expect context to be
  { value : object
  field : CMxCommonApp.CodeLayerField,
  fields : CMxCommonApp.CodeLayerField[],
  transaction : CMxCommonApp.CQATransaction
  invoice : CMxCommonApp.CQAInvoice }
*/ const gradeUpdate = createAsyncThunk(GradeBenchPanel.GRADE_UPDATE, async (changeEvent, { dispatch })=>{
    const { field } = changeEvent.context;
    if (field.key == 'assessment' || field.key == 'role') {
        dispatch(updateOrAddAssessment(changeEvent));
    }
    return null;
});
const updateOrAddAssessment = createAsyncThunk(GradeBenchPanel.UPDATE_OR_ADD_ASSESSMENT, async (changeEvent, { dispatch })=>{
    const { field, fields, value } = changeEvent.context;
    let assessment = '';
    let role = '';
    try {
        if (field.key === 'assessment') {
            assessment = changeEvent.selection;
            const roleField = fields.find((field)=>field.key == 'role');
            let optionsAtTree = getOptionsFromTree(_object_spread_props(_object_spread({}, value), {
                codeAssessment: {
                    assessment
                }
            }), roleField.optionsTree, roleField.optionsTreePath);
            if (optionsAtTree && optionsAtTree.length == 1) {
                role = optionsAtTree[0];
            }
        }
        if (field.key === 'role') {
            var _value_codeAssessment, _value_codeAssessment1;
            assessment = (value === null || value === void 0 ? void 0 : (_value_codeAssessment = value.codeAssessment) === null || _value_codeAssessment === void 0 ? void 0 : _value_codeAssessment.assessment) ? value === null || value === void 0 ? void 0 : (_value_codeAssessment1 = value.codeAssessment) === null || _value_codeAssessment1 === void 0 ? void 0 : _value_codeAssessment1.assessment : null;
            role = changeEvent.selection;
        }
        if (value.codeAssessment === null) {
            return await requestJSON(`/api/cqa-service/v1/${value.codeType}/${value.id}/assessment`, {
                method: Methods.POST,
                body: JSON.stringify({
                    assessment,
                    role
                })
            });
        } else if (value.codeAssessment.role !== role || value.codeAssessment.assessment !== assessment) {
            return await requestJSON(`/api/cqa-service/v1/assessment/${value.codeAssessment.id}`, {
                method: Methods.PUT,
                body: JSON.stringify({
                    assessment,
                    role
                })
            });
        }
    } catch (e) {
        console.log('e', e);
    }
    return null;
});
const addCode = createAsyncThunk(GradeBenchPanel.ADD_CODE, async (addCodePayload, { dispatch })=>{
    if (isSelectionCPT(addCodePayload.codeInfo)) {
        await dispatch(addProcedure(addCodePayload));
    }
    if (isSelectionICD(addCodePayload.codeInfo)) {
        await dispatch(addDiagnosis(addCodePayload));
    }
    if (isSelectionModifier(addCodePayload.codeInfo)) {
        await dispatch(addModifier(addCodePayload));
    }
    return addCodePayload;
});
const addProcedure = createAsyncThunk(GradeBenchPanel.ADD_PROCEDURE_CODE, async (addCodePayload, { dispatch })=>{
    const requestBody = {
        code: addCodePayload.codeInfo.code,
        units: 1,
        sequenceNumber: 0
    };
    // Add spans information if provided
    if (addCodePayload.spans && addCodePayload.documentId && addCodePayload.caseId) {
        requestBody.documentId = Number(addCodePayload.documentId);
        requestBody.caseId = addCodePayload.caseId;
        requestBody.spans = addCodePayload.spans.map((span)=>({
                id: span.id,
                beginOffset: span.beginOffset,
                endOffset: span.endOffset,
                coveredText: span.coveredText,
                attributes: span.attributes
            }));
    }
    return await requestJSON(`/api/cqa-service/v1/transaction/${addCodePayload.context.transaction.id}/procedure`, {
        method: Methods.POST,
        body: JSON.stringify(requestBody)
    });
});
const addDiagnosis = createAsyncThunk(GradeBenchPanel.ADD_DIAGNOSIS_CODE, async (addCodePayload, { dispatch })=>{
    const requestBody = {
        code: addCodePayload.codeInfo.code,
        sequenceNumber: 0
    };
    // Add spans information if provided
    if (addCodePayload.spans && addCodePayload.documentId && addCodePayload.caseId) {
        requestBody.documentId = Number(addCodePayload.documentId);
        requestBody.caseId = addCodePayload.caseId;
        requestBody.spans = addCodePayload.spans.map((span)=>({
                id: span.id,
                beginOffset: span.beginOffset,
                endOffset: span.endOffset,
                coveredText: span.coveredText,
                attributes: span.attributes
            }));
    }
    return await requestJSON(`/api/cqa-service/v1/transaction/${addCodePayload.context.transaction.id}/diagnosis`, {
        method: Methods.POST,
        body: JSON.stringify(requestBody)
    });
});
const addModifier = createAsyncThunk(GradeBenchPanel.ADD_MODIFIER_CODE, async (addCodePayload, { dispatch })=>{
    const requestBody = {
        code: addCodePayload.codeInfo.code,
        sequenceNumber: 0
    };
    // Add spans information if provided
    if (addCodePayload.spans && addCodePayload.documentId && addCodePayload.caseId) {
        requestBody.documentId = Number(addCodePayload.documentId);
        requestBody.caseId = addCodePayload.caseId;
        requestBody.spans = addCodePayload.spans.map((span)=>({
                id: span.id,
                beginOffset: span.beginOffset,
                endOffset: span.endOffset,
                coveredText: span.coveredText,
                attributes: span.attributes
            }));
    }
    return await requestJSON(`/api/cqa-service/v1/transaction/${addCodePayload.context.transaction.id}/modifier`, {
        method: Methods.POST,
        body: JSON.stringify(requestBody)
    });
});
const removeCode = createAction(GradeBenchPanel.REMOVE_CODE);
const resequenceCode = createAsyncThunk(GradeBenchPanel.CODE_EVENT, async (changeEvent, { dispatch })=>{
    const code = changeEvent.code;
    try {
        var _code_units;
        const codeType = code.codeType;
        const units = (_code_units = code.units) !== null && _code_units !== void 0 ? _code_units : undefined;
        return await requestJSON(`/api/cqa-service/v1/${codeType}/${code.id}`, {
            method: Methods.PUT,
            body: JSON.stringify({
                code: code.code,
                sequenceNumber: code.sequenceNumber,
                units: codeType == 'procedure' ? units : undefined
            })
        });
    } catch (e) {
        console.log(e);
    }
    return null;
});
const loadCQAItemFromURL = createAsyncThunk(GradeBenchPanel.LOAD_CQA_WORK_ITEM_FROM_URL, async (match, { dispatch })=>{
    const cqaGraderWorkItem = await requestJSON(`/api/cqa-service/v1/grader-workitem/${match.params.id}`, {
        method: Methods.GET
    });
    dispatch(getDocumentsAsHtmlFromCQAWorkItem(cqaGraderWorkItem.workItem));
    dispatch(fetchCodeDescriptions(cqaGraderWorkItem));
    dispatch(retrieveAssociatedWorkitems(cqaGraderWorkItem.workItem.id));
    // Fetch all rubric attributes for case, codes, and spans in one action
    dispatch(fetchAllAttributes({
        sdCaseId: cqaGraderWorkItem.sdCaseId,
        invoices: cqaGraderWorkItem.invoices,
        looseSpans: cqaGraderWorkItem.looseSpans || []
    }));
    const { ruleCollectionId, serviceLine } = cqaGraderWorkItem.workItem;
    if (ruleCollectionId && serviceLine) {
        dispatch(fetchQaRule({
            qaCollectionUuid: ruleCollectionId,
            level: 3,
            service: serviceLine
        }));
    }
    return cqaGraderWorkItem;
});
const fetchQaRule = createAsyncThunk(GradeBenchPanel.FETCH_QA_RULE, async (qaRuleParams, { dispatch })=>{
    const { qaCollectionUuid, level, service } = qaRuleParams;
    const url = '/api/service-desk/bff';
    const query = {
        operationName: 'SearchQarule',
        variables: {
            request: {
                combinator: 'AND',
                filters: [
                    {
                        key: 'collection_id',
                        operation: 'eq',
                        value: qaCollectionUuid
                    }
                ],
                level
            }
        },
        query: 'mutation SearchQarule($request: SearchQaruleRequest!) {\n  SearchQarule(request: $request) {\n    data\n    total\n    __typename\n  }\n}'
    };
    try {
        var _qaRule_data_project_configuration, _qaRule_data;
        const response = await requestJSON(url, {
            body: JSON.stringify(query),
            method: 'POST'
        });
        const total = response.data.SearchQarule.total;
        const qaRule = total >= 1 ? response.data.SearchQarule.data[0] : null;
        // Always fetch rubric from project configuration when qaRule is loaded
        const rubricVersionId = qaRule === null || qaRule === void 0 ? void 0 : (_qaRule_data = qaRule.data) === null || _qaRule_data === void 0 ? void 0 : (_qaRule_data_project_configuration = _qaRule_data.project_configuration) === null || _qaRule_data_project_configuration === void 0 ? void 0 : _qaRule_data_project_configuration.rubricVersionId;
        if (rubricVersionId) {
            dispatch(fetchRubricVersion({
                versionId: rubricVersionId
            }));
        } else {
            dispatch(clearRubricVersion());
        }
        return qaRule;
    } catch (error) {
        console.error('Failed to SearchQarule in fetchQaRule fn', error);
    }
    return null;
});
const collapseWhitespace = (text)=>{
    return text// First normalize all line endings to \n
    .replace(/\r\n/g, '\n')// Replace 3 or more newlines with exactly 2 newlines (preserving paragraph spacing)
    .replace(/\n{3,}/g, '\n\n').trim();
};
const getDocumentsAsHtmlFromCQAWorkItem = createAsyncThunk(GradeBenchPanel.FETCH_CQA_WORKITEM_DOCUMENTS, async (cqaWorkItem, { dispatch })=>{
    let notes = [];
    for (let note of cqaWorkItem.notes){
        try {
            const endpoint = `/documents/download/${note}/v1`;
            const content = await request(endpoint);
            const text = await content.text();
            const htmlNote = decoratedNoteFactory({
                description: '',
                content: text,
                filename: 'Document',
                type: cqaWorkItem.serviceLine,
                //One of these ids may not be right
                document_id: note,
                id: note,
                filetype: 'text/plain',
                benchmode: BenchMode.GRADE
            });
            if (cqaWorkItem.serviceLine.toLowerCase() == 'surgery') {
                htmlNote.content = collapseWhitespace(htmlNote.content);
            }
            notes.push(htmlNote);
        } catch (e) {
            console.log(`Cases: error fetching document with id : ${note} for workItem ${cqaWorkItem.id}`, e);
        }
    }
    return notes;
});
const createCQATransaction = createAsyncThunk(GradeBenchPanel.CREATE_CQA_TRANSACTION, async (args, { dispatch })=>{
    const url = `/api/cqa-service/v1/invoice/${args.invoiceId}/transaction`;
    return await requestJSON(url, {
        method: 'POST'
    }).catch((error)=>{
        console.error('Failed to createCQATransaction by API call', error);
        return null;
    });
});
const deleteCQATransaction = createAsyncThunk(GradeBenchPanel.DELETE_CQA_TRANSACTION, async (args, { dispatch })=>{
    if (args.original) {
        return false;
    }
    const url = `/api/cqa-service/v1/transaction/${args.transactionId}`;
    return await requestJSON(url, {
        method: 'DELETE'
    }).then(()=>true).catch((error)=>{
        console.error('Failed to deleteCQATransaction by API call', error);
        return false;
    });
});
//This action may not be GradeBench specific however it is currently only called for grading ui
const launchAssociatedWorkItemInGradeBench = createAsyncThunk(GradeBenchPanel.LOAD_ASSOCIATED_WORKITEM, async (params, { dispatch })=>{
    var _cursorAPIContext_worklistContext, _cursorAPIContext_worklistContext1, _cursorAPIContext_worklistContext2, _cursorAPIContext_worklistContext3;
    const { cursorAPIContext, sdWorkitem } = params;
    const launchConfig = {
        serviceLine: sdWorkitem.data.serviceLine,
        caseId: sdWorkitem.data.case_id,
        processId: sdWorkitem.data.cmx_process_id,
        workitemId: sdWorkitem.data.case_id,
        sessionId: cursorAPIContext.sessionId,
        qaCollectionUuid: sdWorkitem.data.qa_collection_id,
        //From capabilities.cursorApiContext
        servicedeskMode: cursorAPIContext.servicedeskMode,
        worklistContext: cursorAPIContext.worklistContext,
        isCodingQuality: (_cursorAPIContext_worklistContext = cursorAPIContext.worklistContext) === null || _cursorAPIContext_worklistContext === void 0 ? void 0 : _cursorAPIContext_worklistContext.isCodingQuality,
        isQualityAssessment: (_cursorAPIContext_worklistContext1 = cursorAPIContext.worklistContext) === null || _cursorAPIContext_worklistContext1 === void 0 ? void 0 : _cursorAPIContext_worklistContext1.isQualityAssessment,
        hasAmplifyCaseRunV2Access: (_cursorAPIContext_worklistContext2 = cursorAPIContext.worklistContext) === null || _cursorAPIContext_worklistContext2 === void 0 ? void 0 : _cursorAPIContext_worklistContext2.hasAmplifyCaseRunV2Access,
        cursorAPIContext: cursorAPIContext,
        isMergedCase: (_cursorAPIContext_worklistContext3 = cursorAPIContext.worklistContext) === null || _cursorAPIContext_worklistContext3 === void 0 ? void 0 : _cursorAPIContext_worklistContext3.isMergedCase
    };
    dispatch(loadWorkitemInAmplify(launchConfig));
    return null;
});
const submitCQAWorkitem = createAsyncThunk(GradeBenchPanel.SUBMIT_CQA_WORKITEM, async (params, { dispatch })=>{
    const { cursorAPIPayload, status } = params;
    const workitemId = cursorAPIPayload.cursorAPIContext.workitemId;
    try {
        var _cursorAPIContext_worklistContext;
        await requestJSON(`/api/cqa-service/v1/grader-workitem/${workitemId}`, {
            method: Methods.PUT,
            body: JSON.stringify({
                status: status
            })
        });
        dispatch(showSubmittedFeedback());
        const { cursorAPIContext } = cursorAPIPayload;
        if (((_cursorAPIContext_worklistContext = cursorAPIContext.worklistContext) === null || _cursorAPIContext_worklistContext === void 0 ? void 0 : _cursorAPIContext_worklistContext.query) == null) {
            const url = buildQualityAssessmentWorklistPath(cursorAPIContext);
            dispatch(navigationIntent({
                intent: 'CASE_DETAIL',
                url
            }));
        } else {
            dispatch(loadNextOrPreviousCase({
                cursorAPIPayload: cursorAPIPayload,
                status: FeedbackMeta.SUCCESS
            }));
        }
        return null;
    } catch (e) {
        dispatch(amplifyFeedback({
            message: 'There was an issue submitting this workitem.',
            meta: FeedbackMeta.ERROR,
            dismissable: true
        }));
    }
});
const resolveCQAWorkitem = createAsyncThunk(GradeBenchPanel.RESOLVE_CQA_WORKITEM, async (_params, { dispatch, getState })=>{
    var _state_gradeBenchPanel_qaRule_data_project_configuration, _state_gradeBenchPanel_qaRule_data, _state_gradeBenchPanel_qaRule;
    const empty = {
        validationErrors: [],
        skipped: true
    };
    const state = getState();
    if (!(state === null || state === void 0 ? void 0 : state.gradeBenchPanel)) return empty;
    const rubricVersionId = (_state_gradeBenchPanel_qaRule = state.gradeBenchPanel.qaRule) === null || _state_gradeBenchPanel_qaRule === void 0 ? void 0 : (_state_gradeBenchPanel_qaRule_data = _state_gradeBenchPanel_qaRule.data) === null || _state_gradeBenchPanel_qaRule_data === void 0 ? void 0 : (_state_gradeBenchPanel_qaRule_data_project_configuration = _state_gradeBenchPanel_qaRule_data.project_configuration) === null || _state_gradeBenchPanel_qaRule_data_project_configuration === void 0 ? void 0 : _state_gradeBenchPanel_qaRule_data_project_configuration.rubricVersionId;
    if (rubricVersionId == null) {
        return empty;
    }
    try {
        var _result_validationErrors;
        const input = gradeBenchStateToResolverInput(state.gradeBenchPanel);
        const result = await requestJSON('/api/cqa-service/v1/resolver/resolve', {
            method: Methods.POST,
            body: JSON.stringify({
                rubricVersionId,
                input
            })
        });
        const validationErrors = (_result_validationErrors = result.validationErrors) !== null && _result_validationErrors !== void 0 ? _result_validationErrors : [];
        return {
            validationErrors,
            skipped: false
        };
    } catch (e) {
        console.error('Error resolving CQA workitem', e);
        return empty;
    }
});
const deleteCQACode = createAsyncThunk(GradeBenchPanel.DELETE_CQA_CODE, async (args, { dispatch })=>{
    if (args.code.original) {
        return false;
    }
    const url = `/api/cqa-service/v1/${args.codeType}/${args.code.id}`;
    return await requestJSON(url, {
        method: 'DELETE'
    }).then(()=>true).catch((error)=>{
        console.error('Failed to deleteCQACode by API call', error);
        return false;
    });
});
//Actions currently not called directly from a component, only dispatched by other actions
const ReduxActions = {
    addModifier,
    addDiagnosis,
    addProcedure,
    updateOrAddAssessment
};
const fetchCodeDescriptions = createAsyncThunk(GradeBenchPanel.FETCH_CQA_CODE_DESCRIPTIONS, async (cqaGraderWorkItem, { dispatch })=>{
    const invoices = cqaGraderWorkItem.invoices;
    const healthSystemTenantId = cqaGraderWorkItem.workItem.healthSystemTenantId;
    if (invoices.length) {
        try {
            const generateCodeRequests = (items, codeType, dictionaryName)=>items.map((item)=>({
                        code: item.code,
                        codeType,
                        effectiveDate: '2022-07-08',
                        dictionaryName,
                        columnName: 'Code'
                    }));
            const codeRequests = invoices.flatMap((invoice)=>invoice.transactions.flatMap((tx)=>[
                        ...generateCodeRequests(tx.procedures, 'CPT', 'CPT Codes'),
                        ...generateCodeRequests(tx.diagnoses, 'ICD', 'ICD Codes'),
                        ...generateCodeRequests(tx.modifiers, 'MODIFIER', 'Modifiers')
                    ]));
            const uniqueRequests = uniqBy(codeRequests, (code)=>`${code.code}-${code.effectiveDate}`);
            const url = `/dictionary/codes/description/v2?tenantId=${healthSystemTenantId}`;
            const descriptions = await requestJSON(url, {
                body: JSON.stringify(uniqueRequests),
                method: 'POST'
            });
            const normalizeDescriptions$1 = normalizeDescriptions(descriptions);
            return normalizeDescriptions$1;
        } catch (e) {
            console.log(e);
        }
    }
    return {};
});
const retrieveAssociatedWorkitems = createAsyncThunk(GradeBenchPanel.RETRIEVE_ASSOCIATED_WORKITEMS, async (workItemId, { dispatch })=>{
    try {
        const cqaWorkItem = await requestJSON(`/api/cqa-service/v1/workitem/${workItemId}`, {
            method: Methods.GET
        });
        if (!(cqaWorkItem === null || cqaWorkItem === void 0 ? void 0 : cqaWorkItem.graders)) {
            console.error('No graders found in workitem response');
            return [];
        }
        const url = '/api/service-desk/bff';
        const caseIds = cqaWorkItem.graders.map((grader)=>grader.sdCaseId);
        if (caseIds.length <= 1) {
            //If the workitem has only 1 grader then it is not a multi-user workitem, so we dont need
            return [];
        }
        const query = {
            operationName: 'SearchWorkItems',
            variables: {
                request: {
                    combinator: 'OR',
                    filters: [
                        {
                            key: 'workitem.case_id',
                            operation: 'in',
                            value: caseIds
                        }
                    ],
                    pagination: {
                        limit: 50,
                        offset: 0
                    },
                    groupByCQAWorkItemId: true
                }
            },
            query: 'mutation SearchWorkItems($request: SearchWorkItemRequest!) { \nSearchWorkItems(request: $request) { \n data \n total \n__typename \n} \n}'
        };
        try {
            const response = await requestJSON(url, {
                body: JSON.stringify(query),
                method: 'POST'
            });
            return response.data.SearchWorkItems.data;
        } catch (error) {
            console.error('Failed to SearchQarule in fetchQaRule fn', error);
        }
    } catch (error) {
        console.error('Failed to retrieve associated workitems:', error);
    }
});
const fetchWorkerInfoForUser = createAsyncThunk(GradeBenchPanel.FETCH_WORKER_INFO_FOR_USER, async (userContext, { getState })=>{
    var _state_ui, _fullContext_user, _fullContext_activeContext;
    const state = getState();
    const fullContext = ((_state_ui = state.ui) === null || _state_ui === void 0 ? void 0 : _state_ui.context) || userContext;
    const url = '/api/service-desk/bff';
    // Handle both full context (with .user) and activeContext (with .userId directly or .user)
    const userId = (fullContext === null || fullContext === void 0 ? void 0 : (_fullContext_user = fullContext.user) === null || _fullContext_user === void 0 ? void 0 : _fullContext_user.userId) || (fullContext === null || fullContext === void 0 ? void 0 : fullContext.userId) || '';
    const tenantId = (fullContext === null || fullContext === void 0 ? void 0 : (_fullContext_activeContext = fullContext.activeContext) === null || _fullContext_activeContext === void 0 ? void 0 : _fullContext_activeContext.tenantId) || (fullContext === null || fullContext === void 0 ? void 0 : fullContext.tenantId) || '';
    const query = {
        operationName: 'QueryWorker',
        variables: {
            workerQuery: {
                query: `Select * from worker where user_id='${userId}' and subtenant_id = '${tenantId}' and status='ACTIVE';`
            }
        },
        query: 'mutation QueryWorker($workerQuery: workerQuery!) { \nQueryWorker(workerQuery: $workerQuery)\n}'
    };
    try {
        const response = await requestJSON(url, {
            body: JSON.stringify(query),
            method: 'POST'
        });
        return response;
    } catch (error) {
        console.error('Failed to fetch worker info:', error);
        return null;
    }
});
const getGraderWorkItemActivity = createAsyncThunk(GradeBenchPanel.FETCH_GRADER_WORK_ITEM_ACTIVITY, async (sdCaseId, { dispatch })=>{
    const graderWorkItemActivityPromise = requestJSON(`/api/cqa-service/v1/activity/grader-workitem/${sdCaseId}`);
    const lookupRefName = CoderNotesLookupTypeName['CQA_CODER_COMMENT'];
    const coderCommentsPath = `/coding-api/coder_notes/v1/?${lookupRefName}=${sdCaseId}&lookup_type=CQA_CODER_COMMENT`;
    const feedbackCommentsPath = `/coding-api/coder_notes/v1/?${lookupRefName}=${sdCaseId}&lookup_type=CQA_FEEDBACK_COMMENT`;
    const coderCommentsPromise = requestJSON(coderCommentsPath, {
        method: Methods.GET
    });
    const feedbackCommentsPromise = requestJSON(feedbackCommentsPath, {
        method: Methods.GET
    });
    const [activity, coderComments, feedbackComments] = await Promise.all([
        graderWorkItemActivityPromise,
        coderCommentsPromise,
        feedbackCommentsPromise
    ]);
    const comments = [
        ...coderComments,
        ...feedbackComments
    ];
    return {
        activity,
        comments
    };
});
const addEmptySpan = createAsyncThunk(GradeBenchPanel.ADD_EMPTY_SPAN, async (spanData)=>{
    try {
        const response = await requestJSON(`/api/cqa-service/v1/spans`, {
            method: Methods.POST,
            body: JSON.stringify(spanData)
        });
        // Map response spans back to spanData structure with updated ID
        const updatedSpans = spanData.spans.map((originalSpan, index)=>{
            var _ref;
            var _response_index;
            return _object_spread_props(_object_spread({}, originalSpan), {
                id: (_ref = (_response_index = response[index]) === null || _response_index === void 0 ? void 0 : _response_index.id) !== null && _ref !== void 0 ? _ref : originalSpan.id
            });
        });
        return _object_spread_props(_object_spread({}, spanData), {
            spans: updatedSpans
        });
    } catch (error) {
        console.error('Failed to add empty span:', error);
        throw error;
    }
});
const attachCodeToSpan = createAsyncThunk(GradeBenchPanel.ATTACH_CODE_TO_SPAN, async (params)=>{
    try {
        const response = await requestJSON(`/api/cqa-service/v1/spans/${params.spanId}/attach-code`, {
            method: Methods.POST,
            body: JSON.stringify({
                codeId: params.codeId,
                codeType: params.codeType,
                caseId: params.caseId,
                documentId: params.documentId
            })
        });
        return response;
    } catch (error) {
        console.error('Failed to attach code to span:', error);
        throw error;
    }
});
const detachCodeFromSpan = createAsyncThunk(GradeBenchPanel.DETACH_CODE_FROM_SPAN, async (params)=>{
    try {
        await requestJSON(`/api/cqa-service/v1/spans/${params.spanId}/detach-code?code_id=${params.codeId}&code_type=${params.codeType}`, {
            method: Methods.DELETE
        });
    } catch (error) {
        console.error('Failed to detach code from span:', error);
        throw error;
    }
});
const deleteSpan = createAsyncThunk(GradeBenchPanel.DELETE_SPAN, async (params)=>{
    try {
        await requestJSON(`/api/cqa-service/v1/spans/${params.spanId}`, {
            method: Methods.DELETE
        });
    } catch (error) {
        console.error('Failed to delete span:', error);
        throw error;
    }
});
const clearRubricVersion = createAction(GradeBenchPanel.CLEAR_RUBRIC_VERSION);
const setValidationResult = createAction('SET_VALIDATION_RESULT');
const setCaseLevelComplete = createAction('SET_CASE_LEVEL_COMPLETE');
const clearDownstreamAttributes = createAction(GradeBenchPanel.CLEAR_DOWNSTREAM_ATTRIBUTES);
const fetchRubricVersion = createAsyncThunk(GradeBenchPanel.FETCH_RUBRIC_VERSION, async (params)=>{
    try {
        const response = await requestJSON(`/api/cqa-service/v1/rubric-versions/${params.versionId}/full`, {
            method: Methods.GET
        });
        return response;
    } catch (error) {
        console.error('Failed to fetch rubric version:', error);
        throw error;
    }
});
const updateAttribute = createAsyncThunk(GradeBenchPanel.UPDATE_ATTRIBUTE, async (params)=>{
    var _params_spanScopeAttributeId;
    const hasAnswerDisplayText = params.answerDisplayText != null && params.answerDisplayText.trim() !== '';
    const hasAnswerValueId = params.answerValueId != null;
    const hasSpanAnnotationIds = params.spanAnnotationIds != null && params.spanAnnotationIds.length > 0;
    // Exactly one answer field must be provided
    const providedCount = (hasAnswerDisplayText ? 1 : 0) + (hasAnswerValueId ? 1 : 0) + (hasSpanAnnotationIds ? 1 : 0);
    if (providedCount !== 1) {
        throw new Error('Exactly one of answerValueId, answerDisplayText, or spanAnnotationIds must be provided');
    }
    let body;
    if (hasAnswerDisplayText) {
        body = {
            questionId: params.questionId,
            answerDisplayText: params.answerDisplayText
        };
    } else if (hasSpanAnnotationIds) {
        body = {
            questionId: params.questionId,
            spanAnnotationIds: params.spanAnnotationIds
        };
    } else {
        body = {
            questionId: params.questionId,
            answerId: params.answerValueId
        };
    }
    const spanScopeAttributeId = (_params_spanScopeAttributeId = params.spanScopeAttributeId) !== null && _params_spanScopeAttributeId !== void 0 ? _params_spanScopeAttributeId : params.parentQuestionId;
    if (spanScopeAttributeId != null) {
        body.spanScopeAttributeId = spanScopeAttributeId;
    }
    try {
        const response = await requestJSON(`/api/cqa-service/v1/attributes/${params.entityType}/${params.entityId}`, {
            method: Methods.POST,
            body: JSON.stringify(body)
        });
        return response;
    } catch (error) {
        console.error('Failed to update attribute:', error);
        throw error;
    }
});
const fetchAttributes = createAsyncThunk(GradeBenchPanel.FETCH_ATTRIBUTES, async (params)=>{
    var _params_spanScopeAttributeId;
    const spanScopeAttributeId = (_params_spanScopeAttributeId = params.spanScopeAttributeId) !== null && _params_spanScopeAttributeId !== void 0 ? _params_spanScopeAttributeId : params.parentQuestionId;
    const query = spanScopeAttributeId != null ? `?spanScopeAttributeId=${spanScopeAttributeId}` : '';
    try {
        const response = await requestJSON(`/api/cqa-service/v1/attributes/${params.entityType}/${params.entityId}${query}`, {
            method: Methods.GET
        });
        return response;
    } catch (error) {
        console.error('Failed to fetch attributes:', error);
        throw error;
    }
});
const deleteAttribute = createAsyncThunk(GradeBenchPanel.DELETE_ATTRIBUTE, async (params)=>{
    var _params_spanScopeAttributeId;
    const spanScopeAttributeId = (_params_spanScopeAttributeId = params.spanScopeAttributeId) !== null && _params_spanScopeAttributeId !== void 0 ? _params_spanScopeAttributeId : params.parentQuestionId;
    const query = spanScopeAttributeId != null ? `?spanScopeAttributeId=${spanScopeAttributeId}` : '';
    try {
        await requestJSON(`/api/cqa-service/v1/attributes/${params.entityType}/${params.entityId}/${params.questionId}${query}`, {
            method: Methods.DELETE
        });
    } catch (error) {
        console.error('Failed to delete attribute:', error);
        throw error;
    }
});
/**
 * Fetches all attributes for a case (case-level + all code-level) in a single action.
 * This is called once when a case is loaded to avoid multiple individual fetches.
 */ const fetchAllAttributes = createAsyncThunk(GradeBenchPanel.FETCH_ALL_ATTRIBUTES, async (params)=>{
    const { sdCaseId, invoices, looseSpans } = params;
    // Collect all code IDs with their entity types
    const codeEntities = [];
    invoices.forEach((invoice)=>{
        invoice.transactions.forEach((transaction)=>{
            transaction.procedures.forEach((p)=>codeEntities.push({
                    entityId: p.id,
                    entityType: 'code_procedure'
                }));
            transaction.diagnoses.forEach((d)=>codeEntities.push({
                    entityId: d.id,
                    entityType: 'code_diagnosis'
                }));
            transaction.modifiers.forEach((m)=>codeEntities.push({
                    entityId: m.id,
                    entityType: 'code_modifier'
                }));
        });
    });
    // Collect all unique span IDs from code spans and loose spans
    const spanIds = new Set();
    invoices.forEach((invoice)=>{
        invoice.transactions.forEach((transaction)=>{
            [
                ...transaction.procedures || [],
                ...transaction.diagnoses || [],
                ...transaction.modifiers || []
            ].forEach((code)=>{
                var _code_spans;
                (_code_spans = code.spans) === null || _code_spans === void 0 ? void 0 : _code_spans.forEach((span)=>spanIds.add(span.id));
            });
        });
    });
    (looseSpans || []).forEach((span)=>spanIds.add(span.id));
    const spanEntities = Array.from(spanIds);
    // Fetch case attributes, all code attributes, and all span attributes in parallel
    const [caseAttributes, ...restResults] = await Promise.all([
        requestJSON(`/api/cqa-service/v1/attributes/case/${sdCaseId}`, {
            method: Methods.GET
        }).catch(()=>[]),
        ...codeEntities.map(({ entityId, entityType })=>requestJSON(`/api/cqa-service/v1/attributes/${entityType}/${entityId}`, {
                method: Methods.GET
            }).catch(()=>[])),
        ...spanEntities.map((spanId)=>requestJSON(`/api/cqa-service/v1/attributes/span/${spanId}`, {
                method: Methods.GET
            }).catch(()=>[]))
    ]);
    const codeAttributeResults = restResults.slice(0, codeEntities.length);
    const spanAttributeResults = restResults.slice(codeEntities.length);
    // Build code attributes map: codeId -> attributes[]
    const codeAttributesMap = {};
    codeEntities.forEach((entity, idx)=>{
        codeAttributesMap[entity.entityId] = codeAttributeResults[idx];
    });
    // Build span attributes map: spanId -> attributes[]
    const spanAttributesMap = {};
    spanEntities.forEach((spanId, idx)=>{
        spanAttributesMap[spanId] = spanAttributeResults[idx];
    });
    return {
        caseAttributes,
        codeAttributesMap,
        spanAttributesMap
    };
});
function revalidateWorkItem() {
    return async (dispatch, getState)=>{
        var _getState_gradeBenchPanel_invoices_, _getState_gradeBenchPanel_invoices, _getState_gradeBenchPanel;
        const sdCaseId = (_getState_gradeBenchPanel = getState().gradeBenchPanel) === null || _getState_gradeBenchPanel === void 0 ? void 0 : (_getState_gradeBenchPanel_invoices = _getState_gradeBenchPanel.invoices) === null || _getState_gradeBenchPanel_invoices === void 0 ? void 0 : (_getState_gradeBenchPanel_invoices_ = _getState_gradeBenchPanel_invoices[0]) === null || _getState_gradeBenchPanel_invoices_ === void 0 ? void 0 : _getState_gradeBenchPanel_invoices_.sdCaseId;
        if (!sdCaseId) return;
        try {
            const validation = await validateWorkflow(sdCaseId);
            dispatch(setValidationResult(validation));
        } catch (error) {
            console.error('Revalidation failed:', error);
        }
    };
}
const addUserActivityComment = createAsyncThunk(GradeBenchPanel.ADD_USER_ACTIVITY_COMMENT, async ({ sdCaseId, comment }, { dispatch })=>{
    const lookupRefName = CoderNotesLookupTypeName['CQA_CODER_COMMENT'];
    const path = `/coding-api/coder_notes/v1/?${lookupRefName}=${sdCaseId}&lookup_type=CQA_CODER_COMMENT`;
    await request(path, {
        method: Methods.POST,
        body: JSON.stringify({
            note_content: comment
        })
    });
    await dispatch(getGraderWorkItemActivity(sdCaseId));
});
/**
 * Calls the workflow evaluation API to evaluate system questions and determine
 * visible questions, filtered answers, and autofilled answers.
 * This is a plain async function (not a Redux thunk) so it can be used as evaluateFn.
 */ async function evaluateWorkflow(request) {
    const result = await requestJSON(`/api/cqa-service/v1/workflows/evaluate`, {
        method: Methods.POST,
        body: JSON.stringify(request)
    });
    if (request.sdCaseId) {
        try {
            const validation = await validateWorkflow(request.sdCaseId);
            result.validation = validation;
        } catch (error) {
            console.error('Validation failed after evaluation:', error);
        }
    }
    return result;
}
/**
 * Calls the workflow validation API to validate all required questions are answered.
 * This is a plain async function (not a Redux thunk) so it can be used as validateFn.
 *
 * @param workItemId - The work item ID (t_work_item.id) to validate
 * @returns Promise resolving to {isValid: boolean, errors: string[]}
 */ async function validateWorkflow(workItemId) {
    return requestJSON(`/api/cqa-service/v1/work-items/${workItemId}/validate`, {
        method: Methods.GET
    });
}

var gradeBenchPanelActions = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ReduxActions: ReduxActions,
  addCode: addCode,
  addEmptySpan: addEmptySpan,
  addUserActivityComment: addUserActivityComment,
  attachCodeToSpan: attachCodeToSpan,
  clearDownstreamAttributes: clearDownstreamAttributes,
  clearRubricVersion: clearRubricVersion,
  createCQATransaction: createCQATransaction,
  deleteAttribute: deleteAttribute,
  deleteCQACode: deleteCQACode,
  deleteCQATransaction: deleteCQATransaction,
  deleteSpan: deleteSpan,
  detachCodeFromSpan: detachCodeFromSpan,
  evaluateWorkflow: evaluateWorkflow,
  fetchAllAttributes: fetchAllAttributes,
  fetchAttributes: fetchAttributes,
  fetchCodeDescriptions: fetchCodeDescriptions,
  fetchQaRule: fetchQaRule,
  fetchRiskCategories: fetchRiskCategories,
  fetchRubricVersion: fetchRubricVersion,
  fetchWorkerInfoForUser: fetchWorkerInfoForUser,
  getDocumentsAsHtmlFromCQAWorkItem: getDocumentsAsHtmlFromCQAWorkItem,
  getGraderWorkItemActivity: getGraderWorkItemActivity,
  gradeUpdate: gradeUpdate,
  launchAssociatedWorkItemInGradeBench: launchAssociatedWorkItemInGradeBench,
  loadCQAItemFromURL: loadCQAItemFromURL,
  removeCode: removeCode,
  resequenceCode: resequenceCode,
  resolveCQAWorkitem: resolveCQAWorkitem,
  retrieveAssociatedWorkitems: retrieveAssociatedWorkitems,
  revalidateWorkItem: revalidateWorkItem,
  setCaseLevelComplete: setCaseLevelComplete,
  setValidationResult: setValidationResult,
  submitCQAWorkitem: submitCQAWorkitem,
  updateAttribute: updateAttribute,
  validateWorkflow: validateWorkflow
});

export { setValidationResult as A, setCaseLevelComplete as B, fetchAllAttributes as C, pathUtils as D, buildQualityAssessmentWorklistPath as E, getOptionsFromTree as F, getValueAtPath as G, gradeBenchPanelActions as H, navigationIntent_action as I, addUserActivityComment as J, evaluateWorkflow as K, launchAssociatedWorkItemInGradeBench as L, removeCode as M, revalidateWorkItem as N, submitCQAWorkitem as O, validateWorkflow as P, ReduxActions as R, addEmptySpan as a, detachCodeFromSpan as b, attachCodeToSpan as c, deleteSpan as d, addCode as e, createCQATransaction as f, gradeUpdate as g, getDocumentsAsHtmlFromCQAWorkItem as h, deleteCQACode as i, resolveCQAWorkitem as j, fetchQaRule as k, loadCQAItemFromURL as l, fetchRiskCategories as m, navigationIntent as n, fetchCodeDescriptions as o, deleteCQATransaction as p, retrieveAssociatedWorkitems as q, resequenceCode as r, fetchWorkerInfoForUser as s, getGraderWorkItemActivity as t, clearRubricVersion as u, fetchRubricVersion as v, updateAttribute as w, fetchAttributes as x, deleteAttribute as y, clearDownstreamAttributes as z };
//# sourceMappingURL=grade-bench-panel-BCC7rg7H.mjs.map
